import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
	Check,
	Copy,
	KeyRound,
	RefreshCw,
	Shield,
	ShieldOff,
	Smartphone,
	Tablet,
	Trash2,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { authClient, useAuth } from "@/auth";
import {
	Alert,
	AlertActions,
	AlertDescription,
	AlertTitle,
} from "@/components/catalyst/Alert";
import { Button } from "@/components/catalyst/Button";
import {
	Dialog,
	DialogActions,
	DialogBody,
	DialogDescription,
	DialogTitle,
} from "@/components/catalyst/Dialog";
import { Divider } from "@/components/catalyst/Divider";
import { Field, Label } from "@/components/catalyst/Fieldset";
import { Heading, Subheading } from "@/components/catalyst/Heading";
import { Input } from "@/components/catalyst/Input";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/catalyst/Table";
import { Text } from "@/components/catalyst/Text";
import { Textarea } from "@/components/catalyst/Textarea";
import { useRefetchUser } from "@/hooks/use-refetch-user";
import { TurnstileProvider, useTurnstile } from "@/hooks/use-turnstile";
import { ConfirmPasswordDialog } from "./ConfirmPasswordDialog";

function SecuritySettingsContent() {
	const session = useAuth();
	const refetchUser = useRefetchUser();
	const { getCaptchaHeaders, reset: resetTurnstile } = useTurnstile();

	// Passkey state
	const [passkeyToDelete, setPasskeyToDelete] = useState<string | null>(null);
	const [isAddingPasskey, setIsAddingPasskey] = useState(false);
	const [newPasskeyName, setNewPasskeyName] = useState("");

	// 2FA state
	const [showEnableDialog, setShowEnableDialog] = useState(false);
	const [showDisableDialog, setShowDisableDialog] = useState(false);
	const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
	const [showBackupCodesDialog, setShowBackupCodesDialog] = useState(false);
	const [backupCodes, setBackupCodes] = useState<string[]>([]);
	const [isPending, setIsPending] = useState(false);
	const [copied, setCopied] = useState(false);

	const isTwoFactorEnabled = session?.user?.twoFactorEnabled ?? false;

	const { data: passkeys, refetch: refetchPasskeys } = useQuery({
		queryKey: ["passkeys"],
		queryFn: async () => {
			const { data } = await authClient.passkey.listUserPasskeys();
			return data || [];
		},
	});

	const handleAddPasskey = () => {
		setNewPasskeyName("");
		setIsAddingPasskey(true);
	};

	const confirmAddPasskey = async () => {
		await authClient.passkey.addPasskey({
			name: newPasskeyName,
			fetchOptions: {
				headers: getCaptchaHeaders(),
				onSuccess: () => {
					toast.success("Passkey registered successfully");
					refetchPasskeys();
					setIsAddingPasskey(false);
				},
				onError: (ctx) => {
					resetTurnstile();
					toast.error(ctx.error.message || "Failed to register passkey");
				},
			},
		});
	};

	const handleDeletePasskey = async () => {
		if (!passkeyToDelete) return;

		await authClient.passkey.deletePasskey({
			id: passkeyToDelete,
			fetchOptions: {
				headers: getCaptchaHeaders(),
				onSuccess: () => {
					toast.success("Passkey deleted successfully");
					refetchPasskeys();
					setPasskeyToDelete(null);
				},
				onError: (ctx) => {
					resetTurnstile();
					toast.error(ctx.error.message || "Failed to delete passkey");
					setPasskeyToDelete(null);
				},
			},
		});
	};

	// 2FA handlers
	const handleEnable2FA = async (password: string) => {
		setIsPending(true);
		await authClient.twoFactor.enable({
			password,
			fetchOptions: {
				headers: getCaptchaHeaders(),
				onSuccess: (ctx) => {
					if (ctx.data?.backupCodes) {
						setBackupCodes(ctx.data.backupCodes);
						setShowEnableDialog(false);
						setShowBackupCodesDialog(true);
					}
					toast.success("Two-factor authentication enabled");
					refetchUser();
					setIsPending(false);
				},
				onError: (ctx) => {
					resetTurnstile();
					toast.error(ctx.error.message || "Failed to enable 2FA");
					setIsPending(false);
				},
			},
		});
	};

	const handleDisable2FA = async (password: string) => {
		setIsPending(true);
		await authClient.twoFactor.disable({
			password,
			fetchOptions: {
				headers: getCaptchaHeaders(),
				onSuccess: () => {
					toast.success("Two-factor authentication disabled");
					setShowDisableDialog(false);
					refetchUser();
					setIsPending(false);
				},
				onError: (ctx) => {
					resetTurnstile();
					toast.error(ctx.error.message || "Failed to disable 2FA");
					setIsPending(false);
				},
			},
		});
	};

	const handleRegenerateBackupCodes = async (password: string) => {
		setIsPending(true);
		await authClient.twoFactor.generateBackupCodes({
			password,
			fetchOptions: {
				headers: getCaptchaHeaders(),
				onSuccess: (ctx) => {
					if (ctx.data?.backupCodes) {
						setBackupCodes(ctx.data.backupCodes);
						setShowRegenerateDialog(false);
						setShowBackupCodesDialog(true);
					}
					toast.success("New backup codes generated");
					setIsPending(false);
				},
				onError: (ctx) => {
					resetTurnstile();
					toast.error(ctx.error.message || "Failed to generate backup codes");
					setIsPending(false);
				},
			},
		});
	};

	return (
		<div className="space-y-6">
			{/* Passkey Name Dialog */}
			<Dialog open={isAddingPasskey} onClose={() => setIsAddingPasskey(false)}>
				<DialogTitle>Name your passkey</DialogTitle>
				<DialogDescription>
					Give your passkey a name to easily identify it later.
				</DialogDescription>
				<DialogBody>
					<Field>
						<Label>Passkey Name</Label>
						<Input
							autoFocus
							value={newPasskeyName}
							onChange={(e) => setNewPasskeyName(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									confirmAddPasskey();
								}
							}}
							placeholder="e.g. My Phone, MacBook Pro"
						/>
					</Field>
				</DialogBody>
				<DialogActions>
					<Button plain onClick={() => setIsAddingPasskey(false)}>
						Cancel
					</Button>
					<Button onClick={confirmAddPasskey}>Continue</Button>
				</DialogActions>
			</Dialog>

			{/* Delete Passkey Alert */}
			<Alert open={!!passkeyToDelete} onClose={() => setPasskeyToDelete(null)}>
				<AlertTitle>Delete Passkey?</AlertTitle>
				<AlertDescription>
					Are you sure you want to delete this passkey? This action cannot be
					undone.
				</AlertDescription>
				<AlertActions>
					<Button plain onClick={() => setPasskeyToDelete(null)}>
						Cancel
					</Button>
					<Button color="red" onClick={handleDeletePasskey}>
						Delete
					</Button>
				</AlertActions>
			</Alert>

			{/* Enable 2FA Dialog */}
			<ConfirmPasswordDialog
				isOpen={showEnableDialog}
				onClose={() => setShowEnableDialog(false)}
				onConfirm={handleEnable2FA}
				isPending={isPending}
				title="Enable Two-Factor Authentication"
				description="Enter your password to enable two-factor authentication. You will receive a code via email each time you sign in."
			/>

			{/* Disable 2FA Dialog */}
			<ConfirmPasswordDialog
				isOpen={showDisableDialog}
				onClose={() => setShowDisableDialog(false)}
				onConfirm={handleDisable2FA}
				isPending={isPending}
				title="Disable Two-Factor Authentication"
				description="Enter your password to disable two-factor authentication. Your account will be less secure."
			/>

			{/* Regenerate Backup Codes Dialog */}
			<ConfirmPasswordDialog
				isOpen={showRegenerateDialog}
				onClose={() => setShowRegenerateDialog(false)}
				onConfirm={handleRegenerateBackupCodes}
				isPending={isPending}
				title="Regenerate Backup Codes"
				description="This will invalidate your existing backup codes and generate new ones. Enter your password to continue."
			/>

			{/* Backup Codes Display Dialog */}
			<Dialog
				open={showBackupCodesDialog}
				onClose={() => setShowBackupCodesDialog(false)}
			>
				<DialogTitle>Save Your Backup Codes</DialogTitle>
				<DialogDescription>
					Store these codes in a safe place. Each code can only be used once to
					recover access to your account if you lose your second factor.
				</DialogDescription>
				<DialogBody>
					<div className="relative">
						<Textarea
							readOnly
							resizable={false}
							value={backupCodes.join("\n")}
							rows={backupCodes.length}
							className="font-mono text-sm"
							onClick={(e) => e.currentTarget.select()}
						/>
						<button
							type="button"
							className="absolute top-2 right-2 p-2 rounded-md hover:bg-base-content/10 transition-colors cursor-pointer"
							onClick={async () => {
								await navigator.clipboard.writeText(backupCodes.join("\n"));
								setCopied(true);
								setTimeout(() => setCopied(false), 2000);
								toast.success("Backup codes copied to clipboard");
							}}
							title="Copy all codes"
						>
							{copied ? (
								<Check className="size-4 text-success" />
							) : (
								<Copy className="size-4 text-base-content/50" />
							)}
						</button>
					</div>
					<Text className="mt-4 text-sm text-warning">
						⚠️ These codes won't be shown again. Make sure to save them now!
					</Text>
				</DialogBody>
				<DialogActions>
					<Button onClick={() => setShowBackupCodesDialog(false)}>
						I've saved my codes
					</Button>
				</DialogActions>
			</Dialog>

			<div>
				<Heading>Security</Heading>
				<Subheading>Manage your security preferences and passkeys.</Subheading>
			</div>

			{/* Two-Factor Authentication Section */}
			<div>
				<div className="flex items-center justify-between mb-4">
					<div className="max-w-xl">
						<Heading level={3}>Two-Factor Authentication</Heading>
						<Text>
							Two-factor authentication (2FA) adds an extra layer of security to
							your account by requiring a code sent to your email in addition to
							your password.
						</Text>
					</div>
					{isTwoFactorEnabled ? (
						<div className="flex gap-2">
							<Button
								plain
								onClick={() => setShowRegenerateDialog(true)}
								title="Regenerate backup codes"
							>
								<RefreshCw className="size-4" />
							</Button>
							<Button color="red" onClick={() => setShowDisableDialog(true)}>
								<ShieldOff className="size-4" />
								Disable
							</Button>
						</div>
					) : (
						<Button
							color="dark/white"
							onClick={() => setShowEnableDialog(true)}
						>
							<Shield className="size-4" />
							Enable
						</Button>
					)}
				</div>
				{isTwoFactorEnabled && (
					<div className="p-4 rounded-lg bg-success/10 border border-success/20">
						<div className="flex items-center gap-2 text-success">
							<Shield className="size-5" />
							<Text className="font-medium text-success">
								Two-factor authentication is enabled
							</Text>
						</div>
					</div>
				)}
			</div>

			<Divider />

			{/* Passkeys Section */}
			<div>
				<div className="flex items-center justify-between mb-4">
					<div className="max-w-xl">
						<Heading level={3}>Passkeys</Heading>
						<Text>
							Passkeys allow you to sign in securely without a password using
							your device's biometrics or a security key.
						</Text>
					</div>
					<Button color="dark/white" onClick={handleAddPasskey}>
						<KeyRound className="size-4" />
						Register New Passkey
					</Button>
				</div>

				{passkeys && passkeys.length > 0 ? (
					<Table>
						<TableHead>
							<TableRow>
								<TableHeader>Name</TableHeader>
								<TableHeader>Created</TableHeader>
								<TableHeader className="text-right">Actions</TableHeader>
							</TableRow>
						</TableHead>
						<TableBody>
							{passkeys.map((key) => (
								<TableRow key={key.id}>
									<TableCell className="font-medium">
										<div className="flex items-center gap-2">
											{/* Attempt to guess icon, default to Key */}
											{key.name?.toLowerCase().includes("phone") ? (
												<Smartphone className="size-4 text-base-content/60" />
											) : key.name?.toLowerCase().includes("pad") ? (
												<Tablet className="size-4 text-base-content/60" />
											) : (
												<KeyRound className="size-4 text-base-content/60" />
											)}
											{key.name || "Unnamed Passkey"}
										</div>
									</TableCell>
									<TableCell>
										{format(new Date(key.createdAt), "MMM d, yyyy")}
									</TableCell>
									<TableCell className="text-right">
										<Button
											plain
											onClick={() => setPasskeyToDelete(key.id)}
											aria-label="Delete passkey"
										>
											<Trash2 className="size-4 text-error" />
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				) : (
					<div className="text-center p-6 border border-dashed border-base-content/10 rounded-lg">
						<Text>No passkeys registered yet.</Text>
					</div>
				)}
			</div>
		</div>
	);
}

export function SecuritySettings() {
	return (
		<TurnstileProvider>
			<SecuritySettingsContent />
		</TurnstileProvider>
	);
}
